import { useState, useCallback, useEffect } from 'react';

const INSURANCE_STORAGE_KEY = (petId: string) => `petso_insurance_${petId}`;
const INSURANCE_UPDATED_EVENT = 'petso_insurance_updated';

export interface InsuranceDocument {
  name: string;
  url: string;
  size?: string;
  type?: string;
  uploadedAt?: string;
}

export interface TravelInsurance {
  provider?: string;
  policyNumber?: string;
  coverageStart?: string;
  coverageEnd?: string;
  coverageDetails?: string;
  emergencyContact?: string;
  document?: InsuranceDocument;
}

export interface HealthInsurance {
  provider?: string;
  policyNumber?: string;
  coverageType?: string;
  coverageStart?: string;
  coverageEnd?: string;
  deductible?: string;
  coverageDetails?: string;
  document?: InsuranceDocument;
}

export interface PetInsurance {
  travel?: TravelInsurance;
  health?: HealthInsurance;
}

function loadInsurance(petId: string): PetInsurance {
  try {
    const stored = localStorage.getItem(INSURANCE_STORAGE_KEY(petId));
    if (stored) {
      const parsed = JSON.parse(stored);
      return typeof parsed === 'object' && parsed !== null ? parsed : {};
    }
  } catch {
    // ignore
  }
  return {};
}

export function usePetInsurance(petId: string) {
  const [insurance, setInsuranceState] = useState<PetInsurance>(() =>
    loadInsurance(petId)
  );

  useEffect(() => {
    setInsuranceState(loadInsurance(petId));
  }, [petId]);

  useEffect(() => {
    const handleUpdated = (e: CustomEvent<{ petId: string }>) => {
      if (e.detail?.petId === petId) {
        setInsuranceState(loadInsurance(petId));
      }
    };
    window.addEventListener(
      INSURANCE_UPDATED_EVENT,
      handleUpdated as (event: Event) => void
    );
    return () =>
      window.removeEventListener(
        INSURANCE_UPDATED_EVENT,
        handleUpdated as (event: Event) => void
      );
  }, [petId]);

  const setInsurance = useCallback(
    (update: PetInsurance | ((prev: PetInsurance) => PetInsurance)) => {
      setInsuranceState((prev) => {
        const next =
          typeof update === 'function' ? update(prev) : { ...prev, ...update };
        try {
          localStorage.setItem(
            INSURANCE_STORAGE_KEY(petId),
            JSON.stringify(next)
          );
          window.dispatchEvent(
            new CustomEvent(INSURANCE_UPDATED_EVENT, { detail: { petId } })
          );
        } catch {
          // ignore
        }
        return next;
      });
    },
    [petId]
  );

  const updateTravelInsurance = useCallback(
    (data: Partial<TravelInsurance>) => {
      setInsurance((prev) => ({
        ...prev,
        travel: { ...prev.travel, ...data },
      }));
    },
    [setInsurance]
  );

  const updateHealthInsurance = useCallback(
    (data: Partial<HealthInsurance>) => {
      setInsurance((prev) => ({
        ...prev,
        health: { ...prev.health, ...data },
      }));
    },
    [setInsurance]
  );

  return {
    insurance,
    setInsurance,
    updateTravelInsurance,
    updateHealthInsurance,
  };
}
